import {TextInput} from '@primer/react'
import {type ChangeEvent, type KeyboardEvent, useCallback, useRef, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants'
import {commitUpdateProjectItemFieldValueMutation} from '../../mutations/update-project-item-field-value'
import type {NumberFieldConfigFragment$key} from './__generated__/NumberFieldConfigFragment.graphql'
import type {NumberFieldFragment$key} from './__generated__/NumberFieldFragment.graphql'
import {FieldWrapper} from './FieldWrapper'
import type {BaseFieldProps} from './Shared'

const NumberFieldFragment = graphql`
  fragment NumberFieldFragment on ProjectV2ItemFieldNumberValue {
    id
    number
    field {
      ...NumberFieldConfigFragment
    }
  }
`

const NumberFieldConfigFragment = graphql`
  fragment NumberFieldConfigFragment on ProjectV2Field {
    id
    name
  }
`

type NumberFieldProps = BaseFieldProps<NumberFieldConfigFragment$key, NumberFieldFragment$key>

export function NumberField({viewerCanUpdate, itemId, projectId, field, value, onIssueUpdate}: NumberFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const environment = useRelayEnvironment()

  const fieldData = useFragment(NumberFieldConfigFragment, field)
  const valueData = useFragment(NumberFieldFragment, value)

  const fieldId = fieldData.id
  const fieldName = fieldData.name

  const [currentValue, setCurrentValue] = useState<number | string>(valueData?.number ? valueData.number : '')

  const commitCurrentValue = useCallback(() => {
    if (typeof currentValue !== 'number') return

    commitUpdateProjectItemFieldValueMutation({
      environment,
      input: {
        fieldId,
        itemId,
        projectId,
        value: {number: currentValue},
      },
      onCompleted: onIssueUpdate,
    })
  }, [currentValue, environment, fieldId, itemId, onIssueUpdate, projectId])

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(e.target.valueAsNumber)
  }, [])

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter') {
        commitCurrentValue()
      }
    },
    [commitCurrentValue],
  )

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        placeholder={LABELS.emptySections.noValue(fieldName)}
        canUpdate={false}
        value={currentValue}
        name={fieldName}
      />
    )

  return (
    <FieldWrapper
      value={currentValue}
      name={fieldName}
      placeholder={LABELS.emptySections.chooseNumber}
      inputRef={inputRef}
      input={
        <TextInput
          ref={inputRef}
          size="small"
          sx={{fontSize: 0}}
          type="number"
          onChange={onChange}
          onBlur={commitCurrentValue}
          onKeyDown={onKeyDown}
          value={currentValue}
        />
      }
    />
  )
}

try{ NumberField.displayName ||= 'NumberField' } catch {}